// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Typography } from '@mui/material';

import {
  FBoxContainer,
  FHButton,
  FHConfirmDialog,
  FHSignatureDialog,
  FProgressBar,
} from '../../../base';
import {
  useValidateAbaTherapyHistory,
  useValidateBackgroundPage,
  useValidateCrisisPlan,
  useValidateDischargeTransistions,
  useValidateInitialAssessment,
  useValidateMedicalHistoryPage,
  useValidateMedications,
} from './hooks';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { treatmentPlanActions, treatmentRecommendationActions } from '../../../../store';
import Recommendation from './components/recommendation';
import { areUserRolesIn } from '../../../../helpers';
import { validateSync } from '../../../../helpers/validation-helpers';
import RecommendationServices from './components/recommendationServices';
import { FORTA_BLACK, FORTA_BLUE } from '../../../../utils/colors';
import {
  FullTPRecommendationSchema,
  LimitedTPRecommendationSchema,
  RecommendationServiceSchema,
} from './utils/validationSchema';
import { BAYS, Roles } from '../../../../constants';
import { TreatmentPlanPages } from '../constants/general';
import { getRecommendationServices } from './utils/general';

import type {
  IRecommendation,
  IRecommendationService,
  IServicesTransformed,
} from '../../../../utils/types';
import type { RootState } from '../../../../store';

function TreatmentRecommendations() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { current: treatmentPlan, loading: treatmentPlanLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const {
    treatmentPlanId: currentTreatmentPlanId,
    recommendation,
    services,
    loading: trLoading,
    errorState,
  } = useSelector((state: RootState) => state.treatmentRecommendations);
  const { loading: abaTherapyLoading } = useSelector(
    (state: RootState) => state.abaTherapyServices,
  );
  const { loading: medicalHistoryLoading, referralHistoryLoading } = useSelector(
    (state: RootState) => state.medicalHistories,
  );
  const { loading: medicationsLoading } = useSelector((state: RootState) => state.medications);
  const { loading: assessmentLoading } = useSelector((state: RootState) => state.assessments);
  const { loading: goalsLoading } = useSelector((state: RootState) => state.goals);
  const { dischargeLoading, reasonsLoading } = useSelector(
    (state: RootState) => state.dischargeTransitions,
  );
  const { loading: crisisPlanLoading } = useSelector((state: RootState) => state.crisisPlans);
  const { loading: ebLoading } = useSelector((state: RootState) => state.educationBackgrounds);
  const { loading: cfbLoading } = useSelector((state: RootState) => state.clientFamilyBackgrounds);
  const isEditable = useTreatmentPlanEditable();
  const [treatmentPlanValidated, setTreatmentPlanValidated] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState<boolean>(false);
  const [transformedServices, setTransformedServices] = useState<IServicesTransformed | null>(null);
  const [mandatoryServiceAdded, setMandatoryServiceAdded] = useState<boolean>(false);

  const tpBay = treatmentPlan?.bay ?? BAYS.BAY_2_6;

  const dataLoading = useMemo(() => {
    return (
      abaTherapyLoading ||
      medicalHistoryLoading ||
      referralHistoryLoading ||
      medicationsLoading ||
      assessmentLoading ||
      goalsLoading ||
      dischargeLoading ||
      reasonsLoading ||
      crisisPlanLoading ||
      ebLoading ||
      cfbLoading ||
      trLoading
    );
  }, [
    abaTherapyLoading,
    medicalHistoryLoading,
    referralHistoryLoading,
    medicationsLoading,
    assessmentLoading,
    goalsLoading,
    dischargeLoading,
    reasonsLoading,
    crisisPlanLoading,
    ebLoading,
    cfbLoading,
    trLoading,
  ]);

  const isABAValid = useValidateAbaTherapyHistory();
  const isBackgroundValid = useValidateBackgroundPage();
  const isMedHistoryValid = useValidateMedicalHistoryPage();
  const isAssessmentValid = useValidateInitialAssessment();
  const isCrisisPlanValid = useValidateCrisisPlan();
  const isDTValid = useValidateDischargeTransistions();
  const isMedicationValid = useValidateMedications();
  // const isGoalValid = useValidateGoals();

  const isTPDataValid = useMemo(() => {
    return (
      isABAValid &&
      isBackgroundValid &&
      isMedHistoryValid &&
      isAssessmentValid &&
      isCrisisPlanValid &&
      // isGoalValid &&
      isDTValid &&
      isMedicationValid
    );
  }, [
    isABAValid,
    isBackgroundValid,
    isMedHistoryValid,
    isAssessmentValid,
    isCrisisPlanValid,
    // isGoalValid,
    isDTValid,
    isMedicationValid,
  ]);

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(treatmentRecommendationActions.resetState());
      return;
    }

    if (treatmentPlan?.id === currentTreatmentPlanId) return;

    dispatch(
      treatmentRecommendationActions.getRecommendation({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );

    dispatch(
      treatmentRecommendationActions.updateState({
        key: 'treatmentPlanId',
        data: treatmentPlan?.id,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    validateTreatmentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation, services, isEditable]);

  useEffect(() => {
    if (!services) return;

    const transformedServices: IServicesTransformed = {};
    services.forEach((service: IRecommendationService) => {
      const serviceCode = Object.hasOwn(service.service_code, 'code')
        ? service.service_code.code
        : service.service_code;
      transformedServices[serviceCode] = {
        ...service,
        service_code: serviceCode,
      };
    });
    setTransformedServices(transformedServices);

    const requiredServices = getRecommendationServices(tpBay).filter((service) => service.required);
    setMandatoryServiceAdded(
      requiredServices.every((SERVICE) =>
        services.some(
          (svc: IRecommendationService) =>
            (Object.hasOwn(svc.service_code, 'code')
              ? svc.service_code.code
              : svc.service_code) === SERVICE?.code,
        ),
      ),
    );
  }, [services, tpBay]);

  useEffect(() => {
    let count = 0;
    for (const key in errorState) {
      count += errorState[key];
    }

    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.TreatmentRecommendation]: count,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorState]);

  const fetchCreateRecommendation = (recommendation: IRecommendation | null) => {
    if (!treatmentPlan?.id || !recommendation) return;

    dispatch(
      treatmentRecommendationActions.createRecommendation({
        treatmentPlanId: treatmentPlan.id,
        data: {
          ...recommendation,
          provision_description: recommendation.provision_description
            ? recommendation.provision_description
            : null,
        },
        getAccessTokenSilently,
      }),
    );
  };

  const fetchUpdateRecommendation = (recommendation: IRecommendation | null) => {
    if (!treatmentPlan?.id || !recommendation?.id) return;

    dispatch(
      treatmentRecommendationActions.updateRecommendation({
        treatmentPlanId: treatmentPlan.id,
        data: {
          ...recommendation,
          provision_description: recommendation.provision_description
            ? recommendation.provision_description
            : null,
        },
        getAccessTokenSilently,
      }),
    );
  };

  const fetchCreateService = (service: IRecommendationService) => {
    if (!treatmentPlan?.id || !recommendation?.id || !service) return;

    dispatch(
      treatmentRecommendationActions.createService({
        treatmentPlanId: treatmentPlan.id,
        treatmentRecommendationId: recommendation.id,
        data: service,
        getAccessTokenSilently,
      }),
    );
  };

  const fetchUpdateService = (service: IRecommendationService) => {
    if (!treatmentPlan?.id || !recommendation?.id || !service?.id) return;

    dispatch(
      treatmentRecommendationActions.updateService({
        treatmentPlanId: treatmentPlan.id,
        treatmentRecommendationId: recommendation.id,
        data: service,
        getAccessTokenSilently,
      }),
    );
  };

  const fetchDeleteService = (serviceId: number) => {
    if (!treatmentPlan?.id || !recommendation?.id || !serviceId) return;

    dispatch(
      treatmentRecommendationActions.removeService({
        treatmentPlanId: treatmentPlan.id,
        treatmentRecommendationId: recommendation.id,
        serviceId: serviceId,
        getAccessTokenSilently,
      }),
    );
  };

  const fetchStopTreatmentPlan = async (signatureValue: string) => {
    if (!treatmentPlan?.id || !signatureValue) return;

    const res = await dispatch(
      treatmentPlanActions.stopTreatmentPlan({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
        data: {
          signature: signatureValue,
          utc_offset_date_end: new Date().getTimezoneOffset(),
          date_treatment_plan_ended: new Date().toISOString(),
        },
      }),
    );

    if (res?.error || !res?.payload) return;

    const { status_id } = res.payload;
    dispatch(treatmentPlanActions.updateTreatmentPlanStatus({ statusId: status_id }));
  };

  const validateRecommendation = (recommendation: IRecommendation | null) => {
    if (!recommendation) return false;

    return (
      validateSync(
        tpBay === BAYS.BAY_2_5 ? LimitedTPRecommendationSchema : FullTPRecommendationSchema,
        recommendation,
      ) || false
    );
  };

  const validateServices = (services: IRecommendationService[]) => {
    const servicesValidated: boolean[] = [];

    services.forEach((service: IRecommendationService) => {
      if (service.hours) {
        const isValid = validateSync(RecommendationServiceSchema, service) || false;
        servicesValidated.push(isValid);
      }
    });

    return servicesValidated?.some((isServiceValidated: boolean) => isServiceValidated);
  };

  const validateTreatmentPlan = () => {
    const isValid =
      validateRecommendation(recommendation) && validateServices(services) && isEditable;

    setTreatmentPlanValidated(isValid);
    return isValid;
  };

  const handleSign = (dataURL: string) => {
    if (!dataURL) return;
    handleConcludeTherapyPlan(dataURL);
  };

  const handleConcludeTherapyPlan = (signatureValue?: string) => {
    const isValidated = validateTreatmentPlan();

    if (!isValidated) return;

    if (signatureValue) {
      fetchStopTreatmentPlan(signatureValue);
    }
    setShowConfirmation(false);
    setShowSignatureDialog(false);
  };

  const handleUpdateRecommendation = (recommendation: IRecommendation | null) => {
    if (!recommendation) return;

    const isValid = validateRecommendation(recommendation);
    if (!isValid) return;
    if (isValid && !Object.hasOwn(recommendation, 'id')) fetchCreateRecommendation(recommendation);
    if (isValid && Object.hasOwn(recommendation, 'id')) fetchUpdateRecommendation(recommendation);
  };

  const handleUpdateService = (service: IRecommendationService) => {
    if (!service) return;

    if (service.hours === null || service.hours === 0) {
      if (service.id) {
        fetchDeleteService(service.id);
      }
      return;
    }

    const isValid =
      Object.hasOwn(service, 'service_code') && validateSync(RecommendationServiceSchema, service);
    if (!isValid) return;
    if (isValid && !Object.hasOwn(service, 'id')) fetchCreateService(service);
    if (isValid && Object.hasOwn(service, 'id')) fetchUpdateService(service);
  };

  return (
    <>
      {dataLoading && <FProgressBar />}
      <FBoxContainer title="Treatment Recommendations" sx={{ mt: 3 }}>
        <Recommendation tpBay={tpBay} onUpdate={handleUpdateRecommendation} />
        <Divider
          sx={{ borderWidth: 3, borderColor: FORTA_BLUE.primary, marginBottom: 3, marginTop: 3 }}
        />
        <RecommendationServices
          values={transformedServices ?? {}}
          onUpdate={handleUpdateService}
        />
      </FBoxContainer>

      {isEditable && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end', mt: 2 }}>
          <FHButton
            data-testid="concludeTherapyPlan"
            disabled={
              !isTPDataValid ||
              !treatmentPlanValidated ||
              treatmentPlanLoading ||
              !mandatoryServiceAdded ||
              areUserRolesIn(user?.roles, [Roles.AssistantAssessor])
            }
            onClick={() => setShowConfirmation(true)}
          >
            CONCLUDE THERAPY PLAN
          </FHButton>
        </Box>
      )}

      <FHConfirmDialog
        open={showConfirmation}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
        content={'Are you sure you want to conclude this treatment plan?'}
        submittext={'CONCLUDE'}
        canceltext={'CANCEL'}
        submitBtnTestId="submitConfirmDialog"
        onClose={() => setShowConfirmation(false)}
        onSubmit={() => {
          setShowSignatureDialog(true);
          setShowConfirmation(false);
        }}
      />
      <FHSignatureDialog
        headerText={'Submit'}
        cancelText="Clear signature"
        open={showSignatureDialog}
        onClose={() => setShowSignatureDialog(false)}
        onSign={handleSign}
        contentRenderer={() => (
          <Box>
            <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
              By signing and submitting this record, I hereby attest that (i) I am the rendering or
              responsible clinician named in the assessment, treatment plan, document and/or other
              records contained and referenced herein; (ii) the aforementioned assessment, treatment
              plan, documents and/or other records accurately and completely reflect my assessment
              based on industry standards; (ii) I have thoroughly reviewed all of the client&apos;s
              assessments, treatment plans, documents and/or other records in making this
              determination; and (iii) I have discussed all applicable findings, outcomes, issues or
              discrepancies with the client and/or client&apos;s legal guardian, as appropriate.
            </Typography>
            <br />
            <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
              I understand that any falsification, omission, or concealment of facts may subject me
              to administrative, civil, or criminal liability as well as termination of my
              employment with the company.
            </Typography>
          </Box>
        )}
      />
    </>
  );
}

export default TreatmentRecommendations;
